// Rev sound system with dynamic pitch shifting based on speed
// Uses Web Audio API directly via SFXManager for unified audio context

export class RevSound {
    constructor(sfxManager) {
        this.sfxManager = sfxManager;
        this.revBuffer = null;
        this.revMaxBuffer = null;
        this.newMileBuffer = null;
        this.revSoundPath = './assets/sfx/rev.mp3';
        this.revMaxSoundPath = './assets/sfx/revMax.mp3';
        this.newMileSoundPath = './assets/sfx/newmile.mp3';
        this.loaded = false;
    }

    async load() {
        if (!this.sfxManager.audioContext) return;

        const ctx = this.sfxManager.audioContext;

        const loadBuffer = async (path) => {
            try {
                const response = await fetch(path, { cache: 'reload' });
                if (!response.ok && response.status !== 206) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const arrayBuffer = await response.arrayBuffer();
                if (arrayBuffer.byteLength === 0) {
                    throw new Error('Empty audio file');
                }
                return await ctx.decodeAudioData(arrayBuffer);
            } catch (error) {
                return null;
            }
        };

        const [revBuffer, revMaxBuffer, newMileBuffer] = await Promise.all([
            loadBuffer(this.revSoundPath),
            loadBuffer(this.revMaxSoundPath),
            loadBuffer(this.newMileSoundPath)
        ]);

        this.revBuffer = revBuffer;
        this.revMaxBuffer = revMaxBuffer;
        this.newMileBuffer = newMileBuffer;
        this.loaded = true;
    }

    _playBuffer(buffer, volume = 1, playbackRate = 1) {
        if (!buffer || !this.sfxManager.audioContext || !this.sfxManager.masterGain) {
            return;
        }

        // Resume context if needed
        if (this.sfxManager.audioContext.state === 'suspended') {
            this.sfxManager.audioContext.resume();
        }

        try {
            const source = this.sfxManager.audioContext.createBufferSource();
            source.buffer = buffer;
            source.playbackRate.value = playbackRate;

            const gainNode = this.sfxManager.audioContext.createGain();
            gainNode.gain.value = Math.min(volume, 3); // Cap at 3x to prevent clipping
            gainNode.connect(this.sfxManager.masterGain);

            source.connect(gainNode);
            source.start(0);
        } catch (e) {
            // Silently fail
        }
    }

    playRev(speedPercentage) {
        if (!this.revBuffer) return;

        const pitch = 0.7 + (Math.random() * 1.1);
        const volume = 0.9 + (speedPercentage * 0.3);

        this._playBuffer(this.revBuffer, volume, pitch);
    }

    playRevMax() {
        if (!this.revMaxBuffer) return;
        // Volume 0 in original code - keeping as silent (just a visual thing?)
        this._playBuffer(this.revMaxBuffer, 0, 0.9);
    }

    playNewMile() {
        if (!this.newMileBuffer) return;
        this._playBuffer(this.newMileBuffer, 3, 1.0);
    }

    dispose() {
        this.revBuffer = null;
        this.revMaxBuffer = null;
        this.newMileBuffer = null;
    }
}
